/**
 * Selo "Serviços financeiros Asaas" — exigência do modelo BaaS do Asaas
 * (Resolução Conjunta BCB nº 16/2025). Deve aparecer em todo fluxo com
 * movimentação ou gestão de valores (checkout, Pix/boleto, financeiro, onboarding).
 *
 * Regras do playbook do Asaas:
 *  - usar somente as URLs homologadas enviadas pelo Asaas (com o nosso id);
 *  - NÃO usar referrerPolicy "no-referrer" na <img>: o Asaas usa o referrer
 *    para verificar que o selo foi carregado;
 *  - o link pode apontar para asaas.com, em nova aba, com rel="noopener noreferrer" no <a>.
 */

const SEAL_ID = "43c6bf87-e86a-49ce-a605-755b1b134c13";
const SEAL_BASE = "https://baas.asaas.com/selos";

export const ASAAS_SEAL_URLS = {
    /** Colorido (azul) — fundos claros */
    positivo: `${SEAL_BASE}/Servicos_financeiros_Asaas-Reduzida-Positivo.svg?id=${SEAL_ID}`,
    /** Preto — fundos claros, versão monocromática */
    preto: `${SEAL_BASE}/Servicos_financeiros_Asaas-Reduzida-Negativo-Preto.svg?id=${SEAL_ID}`,
    /** Branco — fundos escuros */
    branco: `${SEAL_BASE}/Servicos_financeiros_Asaas-Reduzida-Negativo-Branco.svg?id=${SEAL_ID}`,
} as const;

/** Razão social do prestador, conforme a cláusula modelo do playbook. */
export const ASAAS_LEGAL_NAME = "ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.";

export const ASAAS_SUPPORT = {
    phone: "0800 009 0037",
    email: "contato@asaas.com.br",
} as const;

type SealVariant = keyof typeof ASAAS_SEAL_URLS | "auto";

interface AsaasSealProps {
    /** "auto" = colorido no tema claro e branco no tema escuro (classe dark do Tailwind). */
    variant?: SealVariant;
    /** Largura em px; a altura segue a proporção real do SVG (188x69). */
    width?: number;
    className?: string;
}

export function AsaasSeal({ variant = "positivo", width = 140, className = "" }: AsaasSealProps) {
    const height = Math.round((width * 69) / 188);
    const img = (src: string, extra = "") => (
        <img
            src={src}
            alt="Serviços financeiros Asaas"
            width={width}
            height={height}
            className={`inline-block ${extra}`}
            style={{ width, height }}
        />
    );

    return (
        <a
            href="https://www.asaas.com"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex shrink-0 ${className}`}
            title="Serviços financeiros prestados pelo Asaas"
        >
            {variant === "auto" ? (
                <>
                    {img(ASAAS_SEAL_URLS.positivo, "dark:hidden")}
                    {img(ASAAS_SEAL_URLS.branco, "hidden dark:inline-block")}
                </>
            ) : (
                img(ASAAS_SEAL_URLS[variant])
            )}
        </a>
    );
}

interface AsaasFinancialNoticeProps {
    variant?: SealVariant;
    /** Texto principal (padrão em português). */
    notice?: string;
    /** Texto antes dos canais de suporte (padrão em português). */
    supportLabel?: string;
    showSupport?: boolean;
    align?: "center" | "left";
    sealWidth?: number;
    className?: string;
}

/** Selo + aviso de que os serviços financeiros são do Asaas + canais de suporte do Asaas. */
export function AsaasFinancialNotice({
    variant = "auto",
    notice = "Os serviços financeiros e de pagamento são prestados pelo Asaas, instituição de pagamento autorizada pelo Banco Central do Brasil.",
    supportLabel = "Dúvidas sobre o pagamento:",
    showSupport = true,
    align = "center",
    sealWidth = 140,
    className = "",
}: AsaasFinancialNoticeProps) {
    const alignCls = align === "center" ? "items-center text-center" : "items-start text-left";
    return (
        <div className={`flex flex-col gap-2 ${alignCls} ${className}`}>
            <AsaasSeal variant={variant} width={sealWidth} />
            <p className="text-[10px] sm:text-[11px] leading-snug text-gray-400 dark:text-gray-500 max-w-sm">
                {notice}
                {showSupport && (
                    <>
                        {" "}{supportLabel}{" "}
                        <a href="tel:08000090037" className="underline whitespace-nowrap">{ASAAS_SUPPORT.phone}</a>
                        {" · "}
                        <a href={`mailto:${ASAAS_SUPPORT.email}`} className="underline">{ASAAS_SUPPORT.email}</a>
                    </>
                )}
            </p>
        </div>
    );
}

export default AsaasSeal;
